import {
    Api, Currency, CurrencySettings, CurrencyUtil, DisplayType, DisplayValue, FieldUpdateEvent, LogManager, ModelRow,
    Navigation, StringUtil
} from "@mcleod/core";
import { AutogenLayoutOfficePendingPayAdjustment } from "./autogen/AutogenLayoutOfficePendingPayAdjustment";
import {
    ClickEvent, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, Layout, LookupModelSearchEvent,
    LookupModelSelectionEvent, Snackbar, Textbox, ValidationResult
} from "@mcleod/components";
import { CommonDialogs } from "@mcleod/common";
import { READY_TO_PAY_ITEMS, Settlement } from "../Settlement";
import { ImageName } from "@mcleod/images";
import { getPayrollControlBoolean } from "@mcleod/general";

const log = LogManager.getLogger("lme.settlement.officepayroll.OfficePendingPayAdjustment");

export class OfficePendingPayAdjustment extends AutogenLayoutOfficePendingPayAdjustment {

    override async onLoad(): Promise<void> {
        CurrencyUtil.configureCurrencySelector(this.textboxCurrency);
        this.sourceOffPendingDeduct.addAfterExecutionListener((event: DataSourceExecutionEvent) => {
            if (event.getAction() == DataSourceAction.SEARCH) {
                this.buttonViewCommissionsHistory.visible = getPayrollControlBoolean("enable_commissions");
                this.buttonViewCommissionsHistory.enabled = this.activeRow.get("post_module") === "SET";
                Settlement.preventUpdateDuringProcessing(this, "Pending Pay Adjustment", "Office Payroll Processing");
            }
            this.setTitle();
        });
        this.textboxCurrency.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (event.hasSelection()) {
                this.changeCurrencyCode();
            }
        });
        this.textboxPayeeId.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (event.hasSelection() && this.mainDataSource.mode !== DataSourceMode.SEARCH) {
                const selection = event.selection;
                if (this.mainDataSource.mode === DataSourceMode.ADD && CurrencySettings.getSingleton().multiCurrencyEnabled()) {
                    this.activeRow.setValueAndLookupModelData("currency", selection.get("currency"), selection.getLookupModelData("currency"));
                    this.changeCurrencyCode();
                }
                const payeeAccepted = !selection.isNull("off_payee.process_status");
                if (payeeAccepted) {
                    this.textboxPayeeId.imagePreName = ImageName.circleCheck;
                    this.textboxStatus.removeItemsWithValue("Y");
                    this.textboxStatus.setSelectedItemFromValue("N");
                } else if (!payeeAccepted && this.textboxStatus.resolveItems().find(item => item.value == "Y") == null) {
                    this.textboxPayeeId.imagePreName = null;
                    this.textboxStatus.items = READY_TO_PAY_ITEMS;
                }
            }
        });
    }

    private async changeCurrencyCode() {
        const newCurrencyCode = this.textboxCurrency.text;

        const [amount] = [this.activeRow.getCurrency("amount")];

        if (amount) {
            amount.setCurrencyCode(newCurrencyCode);
            this.activeRow.set("amount", amount);
        }
    }

    sourceOffPendingDeductAfterModeChange() {
        if (this.mainDataSource.mode == DataSourceMode.ADD || this.mainDataSource.mode == DataSourceMode.UPDATE) {
            if (!this.activeRow.isNull("off_payee.process_status") && this.activeRow.get("ready_to_pay_flag") !== "Y") {
                this.textboxStatus.removeItemsWithValue("Y");
            }
            this.activeRow.addAfterFieldUpdateListener((event: FieldUpdateEvent) => {
                if (event.fieldName === "units" || event.fieldName === "rate") {
                    const units = this.activeRow.getNumber("units", 0);
                    const rate = this.activeRow.getNumber("rate", 0);
                    const amount = new Currency(units * rate);
                    if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
                        amount.setCurrencyCode(this.textboxCurrency.text);
                    }
                    this.activeRow.set("amount", amount);
                }
                this.setSplitAdjustmentButtonEnabled();
            });
            if (this.mainDataSource.mode === DataSourceMode.UPDATE) {
                if (this.textboxCurrency.visible && this.textboxCurrency.enabled) {
                    this.textboxCurrency.enabled = this.activeRow.isNull("gl_accrual_amt") || this.activeRow.getCurrency("gl_accrual_amt").isZero();
                    if (!this.textboxCurrency.enabled) {
                        this.textboxCurrency.disabledTooltip = "Currency cannot be updated when journal entries have already been created";
                    }
                }
                this.buttonSplitAdjustment.visible = true;

            }
        }
    }

    setSplitAdjustmentButtonEnabled() {
        const beingProcessed = !this.activeRow.isNull("off_payee.process_status") && this.activeRow.get("ready_to_pay_flag") === "Y";
        const summaryOrCheckPrinted = !this.activeRow.isNull("process_status");
        this.buttonSplitAdjustment.enabled = !(beingProcessed || summaryOrCheckPrinted || this.activeRow.get("ready_to_pay_flag") === "V" || this.activeRow.hasChanged());
        if (this.activeRow.originalData?.["ready_to_pay_flag"] === "V") {
            this.buttonSplitAdjustment.disabledTooltip = "Pay Adjustment record has already been voided.";
        } else if (this.activeRow.originalData?.["process_status"] === "C") {
            this.buttonSplitAdjustment.disabledTooltip = "The record has been included on a payment. You cannot change the Pay Adjustment.";
        } else if (this.activeRow.originalData?.["process_status"] === "S") {
            this.buttonSplitAdjustment.disabledTooltip = "The record has been included on a summary. You cannot change the Pay Adjustment.";
        } else if (beingProcessed) {
            this.buttonSplitAdjustment.disabledTooltip = "Pay Adjustment is currently being processed.";
        } else {
            this.buttonSplitAdjustment.disabledTooltip = null;
        }
    }

    static requiresAdjustmentAccount(row: ModelRow) {
        const postModule = row.getString("post_module", "");
        const readyToPay = row.getString("ready_to_pay_flag", "");
        const amount = row.getCurrency("amount");
        const accrualAmount = row.getCurrency("gl_accrual_amt");
        return OfficePendingPayAdjustment.fromAPFUELWIRE(postModule)
            && (accrualAmount != null && !accrualAmount.isZero())
            && (readyToPay == "V" || amount.getAmount() != accrualAmount.getAmount());
    }

    static fromAPFUELWIRE(postModule: string) {
        const modules = ["AP", "FUEL", "WIRE"];
        return modules.includes(postModule);
    }

    protected setTitle() {
        if (this.mainDataSource.mode === DataSourceMode.ADD || this.mainDataSource.mode === DataSourceMode.SEARCH) {
            return;
        }
        const payeeData = this.mainDataSource.activeRow?.getLookupModelData("payee_id");
        const payee = payeeData && payeeData[0] ? " - " + payeeData[0].get("name") : "";
        this.dataHeader.title = this.titleSingular + payee;
    }

    setGlidLookup(textbox: Textbox, id: string) {
        const modelRow = this.mainDataSource.activeRow;
        modelRow.set(textbox.field, id);
        const lookupLayout = Layout.getLayout(textbox.lookupModelLayout);
        lookupLayout.addLayoutLoadListener(() => {
            const filter = { search: id };
            lookupLayout.mainDataSource.search(filter, null, { layoutName: lookupLayout.layoutName }).then(response => {
                if (response?.modelRows?.length === 1) {
                    modelRow.setLookupModelData(textbox.field, response.modelRows[0]);
                } else {
                    const lookupModelData = new ModelRow(modelRow._modelPath, false);
                    lookupModelData.set(textbox.lookupModelResultField, modelRow.get(textbox.field));
                    lookupModelData.set(textbox.lookupModelDisplayField, modelRow.get(textbox.field));
                    modelRow.setLookupModelData(textbox.field, lookupModelData);
                }
                textbox.displayData(modelRow, null, 0);
            });
        })
    }


    /** This is an event handler for the onLookupModelSelection event of textboxDeductCodeId. */
    textboxDeductCodeIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (this.mainDataSource.mode != DataSourceMode.SEARCH && event.hasSelection() === true) {
            if (this.activeRow.originalData != null && !this.validDeductCodeChange(this.activeRow.originalData["deduction_type"], event.selection.get("code_type"))) {
                this.mainDataSource.activeRow.revertToOriginalData("deduct_code_id");
                return;
            }
            this.activeRow.set("short_desc", event.selection.get("short_desc"));
            this.activeRow.set("deduction_type", event.selection.get("code_type"));
            if (!event.selection.isNull("amount")) {
                this.textboxUnits.text = "1.0";
                this.mainDataSource.activeRow.set("rate", event.selection.getCurrency("amount").getAmount());
            }
            if (!event.selection.isNull("glid")) {
                this.setGlidLookup(this.textboxGlid, event.selection.get("glid"));
            }
        }

    }

    validDeductCodeChange(oldType: string, newType: string): boolean {
        if (this.isEarning(oldType) && this.isDeduction(newType) && !this.activeRow.isNull("gl_accrual_amt")) {
            Snackbar.showWarningSnackbar("Accrued pay adjustment can not be changed from a "
                + "reimbursement / earning to a deduction. Please void this entry and re-add to correct", { targetPanel: this });
            return false;
        }
        if (this.isEarning(newType) && this.isDeduction(oldType) && !this.activeRow.isNull("gl_accrual_amt")) {
            Snackbar.showWarningSnackbar("Accrued pay adjustment can not be changed from a "
                + "deduction to a reimbursement / earning. Please void this entry and re-add to correct.", { targetPanel: this });
            return false;
        }
        return true;
    }

    isEarning(type: string): boolean {
        return type == "E" || type == "R";
    }

    isDeduction(type: string): boolean {
        return type == "D" || type == "B";
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxGlid. */
    textboxGlidBeforeLookupModelSearch(event) {
        event.filter.isactive = "Y";
    }

    textboxDeductCodeIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_active = "Y";
    }

    async sourceOffPendingDeductBeforeExecution(event: DataSourceExecutionEvent) {

    }

    async sourceOffPendingDeductAfterExecution(event: DataSourceExecutionEvent) {
        this.setSplitAdjustmentButtonEnabled();
    }

    textboxPayeeIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection() && this.mainDataSource.mode === DataSourceMode.ADD) {
            const selection = event.selection;
            const payeePaymentMethod = selection.get("payment_method");
            if(StringUtil.equalsIgnoreCase(payeePaymentMethod,"U")) {
                this.textboxStatus.selectedItem = this.textboxStatus.items[1];
                this.textboxStatus.enabled = false;
            }else{
                this.textboxStatus.selectedItem = this.textboxStatus.items[0];
                this.textboxStatus.enabled = true;
            }
        }
    }

    async buttonSplitAdjustmentOnClick(event: ClickEvent) {
        this.buttonSplitAdjustment.busy = true;
        if (this.validateSimple()) {
            const inputTextbox = new Textbox({
                captionVisible: true,
                fillRow: true,
                required: true,
                caption: "Amount to Split Off",
                displayType: DisplayType.CURRENCY
            });
            inputTextbox.addlValidationCallback = (value) => {
                const result: ValidationResult = {
                    isValid: true,
                    component: inputTextbox
                };

                // Get the split amount and original amount for validation
                const displayValue = DisplayValue.getCurrencyDisplayValue(value);
                const splitAmountCurrency = CurrencyUtil.getCurrencyObjectFromDataValue(displayValue);
                const originalAmount = this.activeRow.getCurrency("amount");

                // Validate split amount
                if (!splitAmountCurrency || !splitAmountCurrency.amount || splitAmountCurrency.amount <= 0) {
                    result.isValid = false;
                    result.validationMessage = "Split amount must be greater than zero";
                    return result;
                }

                if (!originalAmount || !originalAmount.amount) {
                    result.isValid = false;
                    result.validationMessage = "Original pay adjustment has no amount";
                    return result;
                }

                if (splitAmountCurrency.amount >= originalAmount.amount) {
                    result.isValid = false;
                    result.validationMessage = "Split amount must be less than the original amount";
                    return result;
                }

                return result;
            };
            const dialogResponse = await CommonDialogs.showDialog(inputTextbox, {
                title: "Split Pay Adjustment",
                okDefault: true
            });
            if (!inputTextbox.isEmpty() && !dialogResponse.wasCancelled) {
                const splitAmountCurrency = CurrencyUtil.getCurrencyObjectFromDataValue(inputTextbox.getDataValue());

                try {
                    const response = await Api.post("lme/settlement/split-pay-adjustment", {
                        pay_adjustment_id: this.activeRow.get("id"),
                        split_amount: splitAmountCurrency,
                        is_office: true
                    });
                    const data = response?.data?.[0];
                    if (data) {
                        this.mainDataSource.search(this.mainDataSource.lastSearch);
                        Navigation.navigateTo("lme/settlement/officepayroll/OfficePendingPayAdjustment?mode=update&key=" + data.new_pay_adjustment_id, { newTab: true });
                    }
                } catch (error) {
                    await CommonDialogs.showError(error);
                }
            }
        }
        this.buttonSplitAdjustment.busy = false;
    }

    /** This is an event handler for the onClick event of buttonViewCommissionsHistory. */
    buttonViewCommissionsHistoryOnClick(event: ClickEvent) {
        Navigation.navigateTo("lme/settlement/commissions/CommissionsHistoryList?postKey=" + this.activeRow.get("post_key"), { newTab: true });
    }
}
